import React, { useEffect, useState } from 'react';
import {
  NodeType,
  NODE_TYPES,
  getNodeTypeName,
} from '@/features/connect-dots/types/cytoscape';
import { NODE_STYLE_CONFIG } from '@/features/connect-dots/styles/networkStyles';
import { Toggle } from '@/components/Elements';
import { useTranslation } from 'react-i18next';
import {
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { useNetworkElements } from '@/features/connect-dots/hooks/useNetworkElements';

interface NetworkLegendProps {
  nodeCount?: number;
  nodeTypeFilters: { [key in NodeType]: boolean };
  updateNodeTypeFilter: (nodeType: NodeType, isVisible: boolean) => void;
}

const VISIBLE_NODE_TYPES = [
  NodeType.CROSS,
  NodeType.CROSS_ISSUE,
  NodeType.NORMAL,
  NodeType.SOCIAL_ISSUE,
  NodeType.ASSIST,
  NodeType.THEME,
] as const;

export const NetworkLegend: React.FC<NetworkLegendProps> = ({
  nodeCount = 0,
  nodeTypeFilters,
  updateNodeTypeFilter,
}) => {
  const { t } = useTranslation();
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(false);

  const toggleOpen = () => {
    setIsSettingsMenuOpen(!isSettingsMenuOpen);
  };

  const { nodeTypeCounts } = useNetworkElements();

  const handleInfoIconClick = () => {
    setIsDescriptionVisible(!isDescriptionVisible);
  };

  useEffect(() => {
    if (isDescriptionVisible) {
      const timer = setTimeout(() => {
        setIsDescriptionVisible(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [isDescriptionVisible]);

  return (
    <div className="relative shrink-0">
      {/* トグルボタン */}
      <button
        className="group inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-normal leading-5 text-[#9da5ab] shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-rw-sub/40 focus:ring-offset-1"
        onClick={toggleOpen}
        aria-expanded={isSettingsMenuOpen}
        aria-label={t('タグ表示設定')}
      >
        <span>{t('タグ表示設定')}</span>
        {isSettingsMenuOpen ? (
          <ChevronUpIcon className="size-4 transition-transform" />
        ) : (
          <ChevronDownIcon className="size-4 transition-transform" />
        )}
      </button>

      {/* 設定パネル */}
      {isSettingsMenuOpen && (
        <>
          {/* 背景オーバーレイ (クリックで閉じる) */}
          <div
            className="fixed inset-0 z-10"
            onClick={toggleOpen}
            aria-hidden="true"
          />

          {/* メインパネル */}
          <div className="absolute right-0 top-full z-20 mt-1 w-80 transition-all duration-200 ease-out">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
              {/* ヘッダー */}
              <div className="border-b border-gray-100 bg-gradient-to-r from-rw-main to-rw-sub px-4 py-3">
                <h4 className="text-sm font-semibold text-gray-100">
                  {t('ノードタイプ表示設定')}
                </h4>
                <p className="mt-0.5 text-xs text-gray-200">
                  {t('ネットワークに表示するノードを選択')}
                </p>
              </div>

              {/* ノードタイプリスト */}
              <div className="max-h-96 overflow-y-auto p-3">
                <div className="space-y-1.5">
                  {VISIBLE_NODE_TYPES.map((nodeType) => {
                    const isVisible = nodeTypeFilters[nodeType] !== false;
                    return (
                      <div
                        key={nodeType}
                        className={`group rounded-lg border p-3 transition-all duration-150 ${
                          isVisible
                            ? 'border-gray-200 bg-white shadow-sm hover:shadow-md'
                            : 'border-gray-100 bg-gray-50 opacity-60 hover:opacity-100'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          {/* 左側: カラーインジケータとラベル */}
                          <div className="flex flex-1 items-center gap-2.5">
                            {nodeType !== NodeType.THEME && (
                              <Toggle
                                checked={isVisible}
                                onChange={(checked) =>
                                  updateNodeTypeFilter(nodeType, checked)
                                }
                                size="sm"
                                aria-label={`${t(getNodeTypeName(nodeType))}の表示を切り替え`}
                              />
                            )}
                            <label
                              className="cursor-pointer text-sm font-medium text-gray-700 transition-colors group-hover:text-gray-900"
                              {...(nodeType !== NodeType.THEME
                                ? {
                                    title: `${t(getNodeTypeName(nodeType))}の表示を切り替え`,
                                  }
                                : {})}
                              onClick={() =>
                                updateNodeTypeFilter(nodeType, !isVisible)
                              }
                            >
                              {t(getNodeTypeName(nodeType))}
                            </label>
                          </div>
                          {/* タグ種ごとの数表示 */}
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-sm font-semibold ${
                                isVisible ? 'text-rw-sub' : 'text-gray-400'
                              }`}
                            >
                              {
                                /* ノードタイプごとの数を表示するロジックをここに挿入 */
                                nodeTypeCounts[nodeType] || 0
                              }
                            </span>
                            <span className={'text-sm'}>{t('タグ')}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* フッター */}
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                <div className="flex items-center justify-between text-xs">
                  {/* 説明ボタン */}
                  <button
                    className="group/info flex items-center gap-1.5 rounded-md px-2 py-1.5 text-gray-600 transition-colors hover:bg-white hover:text-rw-main focus:outline-none focus:ring-2 focus:ring-rw-main"
                    onClick={handleInfoIconClick}
                    aria-label={t('タグ種類の説明')}
                  >
                    <InformationCircleIcon className="size-4 transition-transform group-hover/info:scale-110" />
                    <span className="font-medium">{t('タグ種類の説明')}</span>
                  </button>

                  {/* ノード総数 */}
                  <div className="flex items-center gap-1.5 rounded-full bg-white px-2 py-1 shadow-sm">
                    <span className="text-gray-600">{t('総数')}</span>
                    <span className="font-semibold text-rw-sub">
                      {nodeCount}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 説明ツールチップ */}
          {isDescriptionVisible && (
            <div className="absolute right-0 top-full z-30 mt-1 w-80 transition-all duration-200 ease-out">
              <div className="overflow-hidden rounded-xl border border-blue-200 bg-white shadow-2xl">
                <div className="bg-gradient-to-r from-rw-main to-rw-sub px-4 py-2.5">
                  <h5 className="text-sm font-semibold text-white">
                    {t('タグ種類の説明')}
                  </h5>
                </div>
                <div className="max-h-96 overflow-y-auto p-4">
                  <div className="space-y-3">
                    {VISIBLE_NODE_TYPES.map((nodeType) => (
                      <div
                        key={nodeType}
                        className="group rounded-lg border border-gray-100 p-3 transition-all hover:border-blue-200 hover:bg-blue-50"
                      >
                        <div className="flex items-start gap-2.5">
                          <div
                            className="mt-0.5 size-3 shrink-0 rounded-full shadow-sm"
                            style={{
                              backgroundColor:
                                NODE_STYLE_CONFIG[nodeType]['background-color'],
                            }}
                            aria-hidden="true"
                          />
                          <div className="flex-1">
                            <div className="mb-1 text-sm font-semibold text-gray-800">
                              {t(`${NODE_TYPES[nodeType].label}`)}
                            </div>
                            <div className="text-xs leading-relaxed text-gray-600">
                              {t(`${NODE_TYPES[nodeType].description}`)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-center">
                  <button
                    onClick={handleInfoIconClick}
                    className="text-xs font-medium text-gray-500 hover:text-gray-700"
                  >
                    {t('閉じる')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
